import React, { forwardRef } from 'react';
import { type TextareaProps } from './types';
import TextField from './TextField';

/**
 * Textarea component built on TextField with multiline mode.
 * Renders a multi-line input with optional character count (when maxLength is set).
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      placeholder,
      value,
      defaultValue,
      onChange,
      maxLength,
      error = false,
      errorMessage,
      disabled = false,
      readOnly = false,
      className = '',
      width,
      fullWidth = false,
      labelColor,
      rows = 4,
      id,
      onFocus,
      onBlur,
      dataTestId,
    },
    ref
  ) => {
    const textFieldProps: Omit<
      React.ComponentProps<typeof TextField>,
      'ref'
    > = {
      multiline: true,
      rows,
      placeholder: placeholder ?? (typeof label === 'string' ? label : ''),
      error,
      disabled,
      readOnly,
      className,
      fullWidth,
    };
    if (label !== undefined) {
      textFieldProps.label = label;
    }
    if (value !== undefined) {
      textFieldProps.value = value;
    }
    if (defaultValue !== undefined) {
      textFieldProps.defaultValue = defaultValue;
    }
    if (maxLength !== undefined) {
      textFieldProps.maxLength = maxLength;
    }
    if (width !== undefined) {
      textFieldProps.width = width;
    }
    if (labelColor !== undefined) {
      textFieldProps.labelColor = labelColor;
    }
    if (id !== undefined) {
      textFieldProps.id = id;
    }
    if (dataTestId !== undefined) {
      textFieldProps.dataTestId = dataTestId;
    }
    if (errorMessage !== undefined) {
      textFieldProps.errorMessage = errorMessage;
    }
    if (onChange != null) {
      textFieldProps.onChange = (e) =>
        onChange(e as React.ChangeEvent<HTMLTextAreaElement>);
    }
    if (onFocus != null) {
      textFieldProps.onFocus = (e) =>
        onFocus(e as React.FocusEvent<HTMLTextAreaElement>);
    }
    if (onBlur != null) {
      textFieldProps.onBlur = (e) =>
        onBlur(e as React.FocusEvent<HTMLTextAreaElement>);
    }

    return (
      <TextField
        ref={ref as React.Ref<HTMLInputElement | HTMLTextAreaElement>}
        {...textFieldProps}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
